import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

import { useCarouselAutoHeight } from "@/components/Playground/Messages/PlaceholderContainer/Content/PCCarousel/hooks/useCarouselAutoHeight";
import { PCCarouselIndicator } from "@/components/Playground/Messages/PlaceholderContainer/Content/PCCarousel/PCCarouselIndicator";
import { PCCarouselItem } from "@/components/Playground/Messages/PlaceholderContainer/Content/PCCarousel/PCCarouselItem";
import { usePlaceholderContainer } from "@/components/Playground/Messages/PlaceholderContainer/context/placeholder-container-context";
import { ChildMessagesStore } from "@/components/Playground/Messages/PlaceholderContainer/stores/child-messages-store";
import { usePlayground } from "@/components/Playground/playground-store";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { cn } from "@/lib/utils";

/** Shared Notion-inspired dashed container styles */
const dashedContainerClasses = cn(
  "rounded-lg",
  "border border-dashed border-gray-200",
  "bg-stone-50/40",
  "transition-colors duration-200",
);

export const PCCarousel = observer(() => {
  const { placeholderStore, variableSets, readonly, childStoresMap } = usePlaceholderContainer();
  const playground = usePlayground();
  const [api, setApi] = useState<CarouselApi>();
  const { activeSetId } = placeholderStore;

  useCarouselAutoHeight(api);

  // Find current index from activeSetId
  const currentIndex = variableSets.findIndex(s => s.id === activeSetId);

  // Capture initial index once so opts don't change on every slide switch
  const [initialIndex] = useState(() => (currentIndex >= 0 ? currentIndex : 0));

  // Update activeSetId when carousel slides
  useEffect(() => {
    if (!api) {
      return;
    }

    const onSelect = () => {
      const selectedIndex = api.selectedScrollSnap();
      const selectedSet = variableSets[selectedIndex];

      if (selectedSet && selectedSet.id !== activeSetId) {
        placeholderStore.setActiveSetId(selectedSet.id);
        playground.setActiveInputVariableSetId(selectedSet.id);
      }
    };

    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, variableSets, activeSetId]);

  // Scroll to correct slide when activeSetId changes externally
  useEffect(() => {
    if (api && currentIndex >= 0) {
      const currentSnap = api.selectedScrollSnap();
      if (currentSnap !== currentIndex) {
        api.scrollTo(currentIndex);
      }
    }
  }, [api, currentIndex]);

  // No sets: show message + add message button
  if (variableSets.length === 0) {
    const handleAddFirstMessage = () => {
      runInAction(() => {
        const newSetId = playground.addEmptyInputVariableSet();
        placeholderStore.setActiveSetId(newSetId);
        playground.setActiveInputVariableSetId(newSetId);
        const store = new ChildMessagesStore();
        childStoresMap.set(newSetId, store);
        store.addMessage();
        playground.startEditingVariableSet(newSetId);
      });
    };

    return (
      <div className={cn(dashedContainerClasses, "flex flex-col items-center justify-center py-5")}>
        <p className="text-[13px] leading-5 text-gray-400">
          Add test messages to preview what gets injected into this placeholder at runtime.{" "}
          {!readonly && (
            <button
              type="button"
              onClick={handleAddFirstMessage}
              className="inline-flex items-center text-gray-500 underline decoration-gray-300 underline-offset-2 transition-colors hover:text-gray-700 hover:decoration-gray-400"
            >
              Click to add
            </button>
          )}
        </p>
      </div>
    );
  }

  // Single set: wrap in dashed container for visual consistency
  if (variableSets.length === 1) {
    return (
      <div className={cn(dashedContainerClasses, "p-2.5")}>
        <PCCarouselItem setId={variableSets[0].id} />
      </div>
    );
  }

  // Multiple sets: render carousel inside dashed container
  return (
    <div className={cn(dashedContainerClasses, "overflow-hidden")}>
      <Carousel
        setApi={setApi}
        opts={{
          duration: 20,
          startIndex: initialIndex,
          watchDrag: false, // Disable drag to prevent conflicts with child DnD
        }}
        className="w-full"
        isKeyboardDisabled
      >
        <PCCarouselIndicator />

        <div className="px-2.5 pb-2.5">
          <CarouselContent className="-ml-2 items-start">
            {variableSets.map(set => (
              <CarouselItem key={`${set.id}`} className={cn("pl-2")}>
                <PCCarouselItem setId={set.id} key={`${set.id}-${activeSetId}`} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </div>
      </Carousel>
    </div>
  );
});
